import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Eye } from 'lucide-react';
import BrowserControls from './BrowserControls';
import PreviewFrame from './PreviewFrame';
import MobileFrame from './MobileFrame';
import PreviewErrorOverlay from './PreviewErrorOverlay';
import ExpoQRCode from './ExpoQRCode';
import { usePreviewScaling } from '../hooks/usePreviewScaling';
import { useIframeError } from '../hooks/useIframeError';

const STATUS_COPY_MAP = {
  loading: {
    title: 'Preview loading',
    description: 'Waiting for the app to finish loading.',
  },
  sleeping: {
    title: 'Sandbox sleeping',
    description: 'The sandbox went idle to save resources.',
  },
  sleeping_never_started: {
    title: 'Sandbox not started',
    description: 'Run a task or wake the sandbox to generate your first preview.',
  },
  resuming: {
    title: 'Sandbox resuming',
    description: 'The sandbox is waking up. The preview will refresh automatically once it is ready.',
  },
  creating: {
    title: 'Sandbox starting',
    description: 'We are preparing your sandbox environment. The preview will appear when it finishes starting.',
  },
  error: {
    title: 'Sandbox unavailable',
    description: 'We could not start the sandbox. Try waking it again or reset the sandbox from the header menu.',
  },
  not_applicable: {
    title: 'Preview not supported',
    description: 'This workspace does not use sandboxes, so the preview is unavailable.',
  },
  fallback: {
    title: 'Preview unavailable',
    description: 'There is no preview to display right now.',
  },
};

const getStatusCopy = (statusKey, context = {}) => {
  if (statusKey === 'sleeping' && context.sleepReason === 'never_started') {
    return STATUS_COPY_MAP.sleeping_never_started;
  }
  return STATUS_COPY_MAP[statusKey] || STATUS_COPY_MAP.fallback;
};

const renderStatusContent = ({ title, description }) => (
  <div className="text-center text-ui-text-secondary max-w-xs">
    <Eye size={48} className="mx-auto mb-4 opacity-50" />
    <p className="text-sm font-medium text-ui-text-primary">{title}</p>
    <p className="text-xs text-ui-text-tertiary mt-2 leading-relaxed">
      {description}
    </p>
  </div>
);

const PreviewPanel = forwardRef(({ 
  sandboxHost, 
  sandboxStatus,
  setInput, 
  handleProgrammaticSubmit,
  viewMode, 
  onViewModeChange,
  isDesktopView: isDesktopViewProp,
  setIsDesktopView: setIsDesktopViewProp,
  viewDropdownOpen: viewDropdownOpenProp,
  setViewDropdownOpen: setViewDropdownOpenProp,
  showControls = true,
  agentStatus,
  workspace
}, ref) => {
  // Use prop values if provided, otherwise use local state
  const [isDesktopViewLocal, setIsDesktopViewLocal] = useState(true);
  const [viewDropdownOpenLocal, setViewDropdownOpenLocal] = useState(false);
  const iframeRef = useRef(null);
  
  const isDesktopView = isDesktopViewProp !== undefined ? isDesktopViewProp : isDesktopViewLocal;
  const setIsDesktopView = setIsDesktopViewProp || setIsDesktopViewLocal;
  const viewDropdownOpen = viewDropdownOpenProp !== undefined ? viewDropdownOpenProp : viewDropdownOpenLocal;
  const setViewDropdownOpen = setViewDropdownOpenProp || setViewDropdownOpenLocal;
  
  // Use custom hooks
  const {
    previewContainerRef,
    viewportWidth,
    scale,
    scaledHeight
  } = usePreviewScaling(isDesktopView, sandboxHost);
  
  const {
    previewError,
    handleTryToFix,
    handleIframeError,
    loadingState,
    resetLoadingState,
    clearError
  } = useIframeError(sandboxHost, iframeRef, setInput, handleProgrammaticSubmit);

  const sandboxState = sandboxStatus?.status || 'unknown';
  const canRenderPreview = sandboxState === 'ready' && Boolean(sandboxHost);

  const renderStatusMessage = () => {
    const copy = getStatusCopy(sandboxState, { sleepReason: sandboxStatus?.sleepReason });
    return (
      <div className="h-full flex items-center justify-center">
        {renderStatusContent(copy)}
      </div>
    );
  };

  // When the agent starts generating, clear any current error so popups don't show
  useEffect(() => {
    if (agentStatus === 'generating') {
      clearError();
    }
  }, [agentStatus, clearError]);
  
  const handleRefresh = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
    clearError();
    resetLoadingState();
  }, [clearError, resetLoadingState]);

  const handleOpenInNewWindow = useCallback(() => {
    if (sandboxHost) {
      window.open(`https://${sandboxHost}`, '_blank');
    }
  }, [sandboxHost]);
  
  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: handleRefresh
  }), [handleRefresh]);

  return (
    <div className="h-full flex flex-col">
      {showControls && (
        <BrowserControls
          sandboxHost={sandboxHost}
          onRefresh={handleRefresh}
          onOpenInNewWindow={handleOpenInNewWindow}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          isDesktopView={isDesktopView}
          onViewChange={setIsDesktopView}
          viewDropdownOpen={viewDropdownOpen}
          setViewDropdownOpen={setViewDropdownOpen}
        />
      )}
      
      <div className="flex-1 relative overflow-auto bg-slate-50 dark:bg-neutral-950 isolate" ref={previewContainerRef}>
        {canRenderPreview ? (
          <>
            {!isDesktopView && (
              <MobileFrame scale={scale}>
                <PreviewFrame
                  sandboxHost={sandboxHost}
                  isDesktopView={false}
                  scale={1} // Scale is handled by MobileFrame
                  viewportWidth={viewportWidth}
                  scaledHeight={scaledHeight}
                  onError={handleIframeError}
                  iframeRef={iframeRef}
                />
              </MobileFrame>
            )}
            
            {isDesktopView && (
              <PreviewFrame
                sandboxHost={sandboxHost}
                isDesktopView={true}
                scale={scale}
                viewportWidth={viewportWidth}
                scaledHeight={scaledHeight}
                onError={handleIframeError}
                iframeRef={iframeRef}
              />
            )}
            
            {/* Loading overlay - shown by default, removed when ready signal received */}
            {loadingState === 'loading' && (
              <div className="absolute inset-0 z-40 bg-slate-50 dark:bg-neutral-950 flex items-center justify-center">
                {renderStatusContent(getStatusCopy('loading'))}
              </div>
            )}
            {agentStatus !== 'generating' && (
              <PreviewErrorOverlay
                error={previewError}
                onClose={clearError}
                onTryToFix={handleTryToFix}
              />
            )}
            
            {/* Floating QR Code overlay for Expo templates - positioned outside all frames */}
            {workspace?.template_slug === 'expo-template' && (
              <div 
                className="absolute z-50"
                style={{
                  bottom: '16px',
                  right: '16px',
                  // No counter-scaling - keep consistent size across views
                }}
              >
                <ExpoQRCode 
                  workspace={workspace} 
                  overlay={true}
                />
              </div>
            )}
          </>
        ) : (
          renderStatusMessage()
        )}
      </div>
    </div>
  );
});

PreviewPanel.displayName = 'PreviewPanel';

export default PreviewPanel; 